import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

export class LetterHeadA4Template extends BasePdfTemplate {
  override templateName = 'letter-head-a4';
  override templateVersion = '1.0.0';
  override description = 'A4 Size invoice template without printed header';
  override pageSize: 'A4' = 'A4';
  override previewImage = 'assets/sampleoutput/a4-sample.png';

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    console.log(data, 'template data');
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    const businessName = storename?.name || data.businessName || '';

    const formatDate = this.formatDate.bind(this);

    const items = data.items || [];
    const itemsPerPage = 20;
    const chunks = [];
    for (let i = 0; i < items.length; i += itemsPerPage) {
      const chunk = items.slice(i, i + itemsPerPage);
      // Add a little empty space after the last item
      if (i + itemsPerPage >= items.length) {
        if (chunk.length < itemsPerPage) chunk.push({ isEmpty: true } as any);
      }
      chunks.push(chunk);
    }

    // const grossAmount = items.reduce((sum, item) => sum + (item.total || 0), 0);
    // Free items are excluded from the gross amount
    const grossAmount = items.reduce(
      (sum, item: any) =>
        Number(item.is_free) === 1 ? sum : sum + (item.total || 0),
      0,
    );

    // Free items summary (qty + value at original price / MRP)
    let totalFreeQty = 0;
    let totalFreeAmount = 0;
    items.forEach((item: any) => {
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });
    const hasFreeItems = totalFreeQty > 0;

    const salesTables: any[] = chunks.map((chunk, index) => ({
      table: {
        headerRows: 1,
        widths: [25, '*', 40, 50, 30, 50, 35, 60],
        body: [
          [
            {
              text: 'S.No.',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'ITEM DESCRIPTION',
              style: 'tableHeader',
              alignment: 'left',
              bold: true,
            },
            {
              text: 'HSN',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'QTY',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'UNIT',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'RATE',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'GST',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'AMOUNT',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
          ],
          ...chunk.map((item: any, i: number) => {
            if (item.isEmpty) {
              return [
                { text: '', margin: [0, 10, 0, 10] },
                { text: '' },
                { text: '' },
                { text: '' },
                { text: '' },
                { text: '' },
                { text: '' },
                { text: '' },
              ];
            }
            const taxableAmount = item.total || 0;
            // Selected mapping (variants / parameters) — one per line under the item name
            const mappingLines = getSelectedMappingLines(item);
            return [
              {
                text: i + 1 + index * itemsPerPage,
                alignment: 'center',
                margin: [0, 2, 0, 2],
                fontSize: 10,
              },
              // {
              //   text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`,
              //   alignment: 'left',
              //   margin: [0, 2, 0, 2],
              //   fontSize: 10,
              // },
              mappingLines.length
                ? {
                  stack: [
                    {
                      text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`,
                      alignment: 'left',
                      fontSize: 10,
                    },
                    ...mappingLines.map((line) => ({
                      text: line,
                      alignment: 'left',
                      fontSize: 8,
                    })),
                  ],
                  margin: [0, 2, 0, 2],
                }
                : {
                  text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`,
                  alignment: 'left',
                  margin: [0, 2, 0, 2],
                  fontSize: 10,
                },
              {
                text: (item.hsncode || item.hsn || '')
                  .toString()
                  .substring(0, 6),
                alignment: 'center',
                margin: [0, 2, 0, 2],
                fontSize: 10,
              },
              {
                text: `${item.quantity}`,
                alignment: 'center',
                margin: [0, 2, 0, 2],
                bold: true,
                fontSize: 11,
              },
              {
                // text: `${item.unit?.name || 'No'}`,
                text: `${getItemUnitName(item) || 'No'}`,
                alignment: 'center',
                margin: [0, 2, 0, 2],
                fontSize: 10,
              },
              {
                text: item.price.toFixed(2),
                alignment: 'center',
                margin: [0, 2, 0, 2],
                fontSize: 10,
              },
              {
                text:
                  Number(item.is_free) === 1
                    ? '0%'
                    : `${item.taxPercent || item.taxRate || 0}%`,
                alignment: 'center',
                margin: [0, 2, 0, 2],
                fontSize: 10,
              },
              {
                text:
                  Number(item.is_free) === 1
                    ? '0.00'
                    : taxableAmount.toFixed(2),
                alignment: 'right',
                margin: [0, 2, 0, 2],
                fontSize: 10,
              },
            ];
          }),
        ],
      },
      layout: {
        hLineWidth: (i: any, node: any) =>
          i === 0 || i === 1 || i === node.table.body.length ? 0.5 : 0,
        vLineWidth: (i: any) => 0.5,
        hLineColor: () => 'black',
        vLineColor: () => 'black',
        paddingLeft: () => 4,
        paddingRight: () => 4,
      },
      margin: [0, 0, 0, 0],
    }));

    return {
      pageSize: 'A4',
      pageMargins: [15, 230, 15, 10],

      header: function (currentPage: number, pageCount: number) {
        return {
          stack: [
            {
              table: {
                widths: ['*', 210],
                body: [
                  [
                    {
                      stack: [
                        {
                          text: `${data.customerName || data.party?.name || ''}`,
                          bold: true,
                          fontSize: 11,
                          margin: [0, 0, 0, 2],
                        },
                        {
                          text: `${data.party?.address || data.customerAddress?.line1 || ''}`,
                          margin: [0, 0, 0, 2],
                        },
                        {
                          text: `Tel : ${data.customerMobile || data.party?.mobile || ''}`,
                          margin: [0, 0, 0, 2],
                        },
                        {
                          text: `GSTIN : ${data.customerGSTIN || data.party?.gstin || ''}`,
                          bold: true,
                        },
                      ],
                      margin: [8, 8, 8, 8],
                    },
                    {
                      table: {
                        widths: [70, '*'],
                        body: [
                          [
                            {
                              text: 'INVOICE',
                              bold: true,
                              margin: [8, 5, 8, 5],
                              border: [false, false, false, true],
                            },
                            {
                              text: data.invoiceNumber || '-',
                              bold: true,
                              alignment: 'right',
                              margin: [8, 5, 8, 5],
                              border: [true, false, false, true],
                            },
                          ],
                          [
                            {
                              text: 'Dated',
                              margin: [8, 5, 8, 5],
                              border: [false, false, false, true],
                            },
                            {
                              text: formatDate(
                                (data.invoiceDate || data.invoicedate || '') as
                                  | string
                                  | Date,
                              ),
                              alignment: 'right',
                              margin: [8, 5, 8, 5],
                              border: [true, false, false, true],
                            },
                          ],
                          [
                            {
                              text: 'Terms Of Payment',
                              margin: [8, 5, 8, 5],
                              border: [false, false, false, false],
                            },
                            {
                              text: data.paymentMethod || 'Credit',
                              alignment: 'right',
                              margin: [8, 5, 8, 5],
                              border: [true, false, false, false],
                            },
                          ],
                        ],
                      },
                      layout: {
                        hLineWidth: () => 0.5,
                        vLineWidth: () => 0.5,
                        hLineColor: () => 'black',
                        vLineColor: () => 'black',
                      },
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
                hLineColor: () => 'black',
                vLineColor: () => 'black',
              },
              margin: [15, 125, 15, 0],
            },
          ],
        };
      },
      content: [
        ...salesTables,
        {
          table: {
            widths: ['*', 60],
            body: [
              [
                {
                  stack: [
                    // Free items summary line (only when free items exist)
                    ...(hasFreeItems
                      ? [
                        {
                          text: `Free Items : ${totalFreeQty}`,
                          alignment: 'right',
                          margin: [0, 2, 8, 2],
                          bold: true,
                          fontSize: 11,
                        },
                      ]
                      : []),
                    {
                      text: 'Gross Amount',
                      alignment: 'right',
                      margin: [0, 2, 8, 2],
                      bold: true,
                      fontSize: 11,
                    },
                    {
                      text: 'SGST (9) %',
                      alignment: 'right',
                      margin: [0, 2, 8, 2],
                      bold: true,
                      fontSize: 11,
                    },
                    {
                      text: 'CGST (9) %',
                      alignment: 'right',
                      margin: [0, 2, 8, 2],
                      bold: true,
                      fontSize: 11,
                    },
                    {
                      text: 'Rounded Off',
                      alignment: 'right',
                      margin: [0, 2, 8, 2],
                      bold: true,
                      fontSize: 11,
                    },
                  ],
                  border: [true, false, true, false],
                },
                {
                  stack: [
                    // Free items value (only when free items exist)
                    ...(hasFreeItems
                      ? [
                        {
                          text: totalFreeAmount.toFixed(2),
                          alignment: 'right',
                          margin: [0, 2, 4, 2],
                          bold: true,
                          fontSize: 11,
                        },
                      ]
                      : []),
                    {
                      text: grossAmount.toFixed(2),
                      alignment: 'right',
                      margin: [0, 2, 4, 2],
                      bold: true,
                      fontSize: 11,
                    },
                    {
                      text: (data.sgst || 0).toFixed(2),
                      alignment: 'right',
                      margin: [0, 2, 4, 2],
                      bold: true,
                      fontSize: 11,
                    },
                    {
                      text: (data.cgst || 0).toFixed(2),
                      alignment: 'right',
                      margin: [0, 2, 4, 2],
                      bold: true,
                      fontSize: 11,
                    },
                    {
                      text: (data.roundOff || data.roundOffAmount || 0).toFixed(
                        2,
                      ),
                      alignment: 'right',
                      margin: [0, 2, 4, 2],
                      bold: true,
                      fontSize: 11,
                    },
                  ],
                  border: [false, false, true, false],
                },
              ],
              [
                {
                  columns: [
                    {
                      stack: [
                        {
                          text: 'Amount Chargeable (in words)',
                          margin: [4, 4, 0, 0],
                        },
                        {
                          text: `${this.numberToWords(this.getGrandTotal(data)).toUpperCase()} ONLY`,
                          bold: true,
                          margin: [4, 4, 0, 4],
                        },
                      ],
                      width: '*',
                    },
                    {
                      text: 'TOTAL',
                      alignment: 'right',
                      bold: true,
                      margin: [0, 8, 8, 0],
                      width: 'auto',
                      fontSize: 12,
                    },
                  ],
                  border: [true, true, true, true],
                },
                {
                  text: this.getGrandTotal(data).toFixed(2),
                  alignment: 'right',
                  bold: true,
                  margin: [0, 8, 4, 0],
                  fontSize: 12,
                  border: [false, true, true, true],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: (i, node) => (i === 0 ? 0 : 0.5),
            vLineWidth: () => 0.5,
            hLineColor: () => 'black',
            vLineColor: () => 'black',
          },
        },
        {
          table: {
            widths: [200, '*', 160],
            body: [
              [
                {
                  stack: [
                    {
                      text: "Company's Bank Details :",
                      bold: true,
                      margin: [4, 4, 0, 4],
                    },
                    {
                      table: {
                        widths: [65, '*'],
                        body: [
                          [
                            { text: 'Bank Name', fontSize: 9, bold: true },
                            {
                              text: `: ${data.bankDetails?.bankName || ''}`,
                              fontSize: 9,
                            },
                          ],
                          [
                            { text: 'Branch Name', fontSize: 9, bold: true },
                            {
                              text: `: ${(data.bankDetails as any)?.branch || (data.bankDetails as any)?.branchName || ''}`,
                              fontSize: 9,
                            },
                          ],
                          [
                            { text: 'Account No', fontSize: 9, bold: true },
                            {
                              text: `: ${data.bankDetails?.accountNumber || ''}`,
                              fontSize: 9,
                            },
                          ],
                          [
                            { text: 'IFSC Code', fontSize: 9, bold: true },
                            {
                              text: `: ${data.bankDetails?.ifscCode || ''}`,
                              fontSize: 9,
                            },
                          ],
                        ],
                      },
                      layout: 'noBorders',
                      margin: [4, 0, 0, 4],
                    },
                  ],
                  border: [true, false, true, true],
                },
                {
                  stack: [
                    { text: '', margin: [0, 40, 0, 0] },
                    {
                      text: "Customer's Seal and Signature:",
                      alignment: 'center',
                    },
                  ],
                  border: [false, false, true, true],
                },
                {
                  stack: [
                    {
                      text: `For ${businessName.toUpperCase()}`,
                      alignment: 'center',
                      bold: true,
                      margin: [0, 4, 0, 0],
                    },
                    (data as any).signature || (data as any).business?.signature
                      ? {
                          image:
                            (data as any).signature ||
                            (data as any).business?.signature,
                          width: 80,
                          height: 40,
                          alignment: 'center',
                          margin: [0, 5, 0, 5],
                        }
                      : { text: '', margin: [0, 40, 0, 0] },
                    {
                      text: 'Authorised Signatory',
                      alignment: 'center',
                      margin: [0, 0, 0, 4],
                    },
                  ],
                  border: [false, false, true, true],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: (i) => (i === 0 ? 0 : 0.5),
            vLineWidth: (i) => 0.5,
            hLineColor: () => 'black',
            vLineColor: () => 'black',
          },
          margin: [0, 0, 0, 10],
        },
      ],
    };
  }
  // Calculate final amount (grand total - overall discount - loyalty discount)
  private getGrandTotal(invoice: InvoiceData): number {
    return (
      (invoice.totalAmount || invoice.grandTotal || 0) -
      (invoice.overallDiscount || 0) -
      (Number(invoice.loyaltyAmount) || 0)
    );
  }

  private numberToWords(num: number): string {
    if (!isFinite(num)) return '';
    const ones = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const tens = [
      '',
      '',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    const convertLessThanThousand = (n: number): string => {
      n = Math.floor(n);
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
        );
      const h = Math.floor(n / 100);
      const rem = n % 100;
      return (
        ones[h] +
        ' Hundred' +
        (rem !== 0 ? ' ' + convertLessThanThousand(rem) : '')
      );
    };

    let n = Math.floor(Math.abs(num));
    if (n === 0) return 'Zero';

    const parts: string[] = [];

    const crore = Math.floor(n / 10000000);
    if (crore) {
      parts.push(convertLessThanThousand(crore) + ' Crore');
      n = n % 10000000;
    }

    const lakh = Math.floor(n / 100000);
    if (lakh) {
      parts.push(convertLessThanThousand(lakh) + ' Lakh');
      n = n % 100000;
    }

    const thousand = Math.floor(n / 1000);
    if (thousand) {
      parts.push(convertLessThanThousand(thousand) + ' Thousand');
      n = n % 1000;
    }

    if (n) {
      parts.push(convertLessThanThousand(n));
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }
}
